'use client';

import React from 'react';
import Link from 'next/link';
import { HiOutlineGlobe } from '@react-icons/all-files/hi/HiOutlineGlobe';
import { HiOutlineTrendingUp } from '@react-icons/all-files/hi/HiOutlineTrendingUp';
import { HiOutlineHashtag } from '@react-icons/all-files/hi/HiOutlineHashtag';
import { HiOutlineLink } from '@react-icons/all-files/hi/HiOutlineLink';
import { HiOutlineUser } from '@react-icons/all-files/hi/HiOutlineUser';
import { HiOutlineViewGrid } from '@react-icons/all-files/hi/HiOutlineViewGrid';
import { HiOutlineSparkles } from '@react-icons/all-files/hi/HiOutlineSparkles';
import { HiOutlinePhotograph } from '@react-icons/all-files/hi/HiOutlinePhotograph';
import { HiOutlineCollection } from '@react-icons/all-files/hi/HiOutlineCollection';
import { HiOutlineChatAlt2 } from '@react-icons/all-files/hi/HiOutlineChatAlt2';
import { HiOutlinePlay } from '@react-icons/all-files/hi/HiOutlinePlay';
import { HiOutlineAnnotation } from '@react-icons/all-files/hi/HiOutlineAnnotation';
import { HiOutlineEmojiHappy } from '@react-icons/all-files/hi/HiOutlineEmojiHappy';
import { HiOutlineChat } from '@react-icons/all-files/hi/HiOutlineChat';
import { HiOutlineChatAlt } from '@react-icons/all-files/hi/HiOutlineChatAlt';
import { HiOutlineUsers } from '@react-icons/all-files/hi/HiOutlineUsers';
import { HiOutlineFire } from '@react-icons/all-files/hi/HiOutlineFire';
import { HiOutlinePhone } from '@react-icons/all-files/hi/HiOutlinePhone';
import { HiOutlineThumbUp } from '@react-icons/all-files/hi/HiOutlineThumbUp';
import { HiOutlineCamera } from '@react-icons/all-files/hi/HiOutlineCamera';
import { HiOutlineMail } from '@react-icons/all-files/hi/HiOutlineMail';
import { HiOutlineLightningBolt } from '@react-icons/all-files/hi/HiOutlineLightningBolt';
import { HiOutlineCode } from '@react-icons/all-files/hi/HiOutlineCode';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import {
  StyledHero,
  StyledHeroInner,
  StyledH1,
  StyledSubtitle,
  StyledToolGrid,
  StyledToolGridInner,
  StyledToolCard,
  StyledCardFooter,
  StyledUseToolLink,
  StyledComingSoonBadge,
} from './SocialMediaHub.styles';

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'instagram-story-maker': <HiOutlineSparkles />,
  'engagement-rate-calculator': <HiOutlineGlobe />,
  'follower-growth-calculator': <HiOutlineTrendingUp />,
  'hashtag-generator': <HiOutlineHashtag />,
  'utm-link-builder': <HiOutlineLink />,
  'bio-link-builder': <HiOutlineUser />,
  'instagram-grid-splitter': <HiOutlineViewGrid />,
  'instagram-fonts-generator': <HiOutlineSparkles />,
  'text-behind-image': <HiOutlinePhotograph />,
  'instagram-carousel-maker': <HiOutlineCollection />,
  'fake-tweet-generator': <HiOutlineChatAlt2 />,
  'fake-whatsapp-chat-generator': <HiOutlineChat />,
  'fake-telegram-chat-generator': <HiOutlineChatAlt />,
  'fake-messenger-chat-generator': <HiOutlineUsers />,
  'fake-reddit-post-generator': <HiOutlineFire />,
  'fake-imessage-generator': <HiOutlinePhone />,
  'fake-facebook-post-generator': <HiOutlineThumbUp />,
  'fake-instagram-post-generator': <HiOutlineCamera />,
  'fake-instagram-dm-generator': <HiOutlineMail />,
  'fake-snapchat-generator': <HiOutlineLightningBolt />,
  'fake-chatgpt-generator': <HiOutlineCode />,
  'fake-gemini-generator': <HiOutlineSparkles />,
  'fake-ai-overview-generator': <HiOutlineSearch />,
  'youtube-thumbnail-maker': <HiOutlinePlay />,
  'quote-card-maker': <HiOutlineAnnotation />,
  'meme-generator': <HiOutlineEmojiHappy />,
  'social-media-image-resizer': <HiOutlinePhotograph />,
};

interface ITool {
  slug: string;
  name: string;
  description: string;
  comingSoon?: boolean;
}

interface IProps {
  tools: ITool[];
}

export const SocialMediaHub = ({ tools }: IProps) => {
  return (
    <>
      <StyledHero>
        <StyledHeroInner>
          <StyledH1>Free Social Media Tools</StyledH1>
          <StyledSubtitle>
            Free calculators to measure your social media engagement rate
            and project follower growth across all major platforms.
          </StyledSubtitle>
        </StyledHeroInner>
      </StyledHero>

      <StyledToolGrid>
        <StyledToolGridInner>
          {tools.map((tool) => (
            <StyledToolCard key={tool.slug} available={!tool.comingSoon}>
              <div>
                <span className="tool-icon">{TOOL_ICONS[tool.slug]}</span>
                <h2>{tool.name}</h2>
                <p>{tool.description}</p>
              </div>
              <StyledCardFooter>
                {!tool.comingSoon ? (
                  <StyledUseToolLink as={Link} href={`/free-tools/social-media/${tool.slug}`}>
                    Use Tool →
                  </StyledUseToolLink>
                ) : (
                  <StyledComingSoonBadge>Coming soon</StyledComingSoonBadge>
                )}
              </StyledCardFooter>
            </StyledToolCard>
          ))}
        </StyledToolGridInner>
      </StyledToolGrid>
    </>
  );
};
